import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { graphqlApi } from '../../services/graphql';
import { CANCELLED, SocialConnectResult } from '../../services/socialConnect';

/**
 * How long an outcome stays on screen before the popup closes itself.
 *
 * Errors linger longer than successes, but both close: leaving the popup open
 * on failure parked a dead window over the opener, hiding the toast that
 * carries the actionable message.
 */
const CLOSE_DELAY_MS = { success: 1500, error: 2500 };

/**
 * OAuth callback page for social media connect (opened in popup).
 * Receives the auth code, exchanges it via the existing backend GraphQL mutations,
 * then posts the result back to the opener window.
 */
const SocialConnectCallback: React.FC = () => {
  const { t } = useTranslation('profile');
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('');

  /**
   * Turn a backend error into something worth reading.
   *
   * The backend reports known conditions as machine sentinels so each caller
   * can translate them. This page was not translating any of them, so a user
   * whose Instagram account is personal saw the literal string
   * "INSTAGRAM_NOT_PROFESSIONAL".
   *
   * The sentinel itself still goes to the opener untouched — that is what its
   * own mapping matches on.
   */
  const humanise = useCallback(
    (raw: string): string => {
      if (raw === 'INSTAGRAM_NOT_PROFESSIONAL') {
        // Same wording as the opener's toast, from the same key, so the two do
        // not drift apart.
        return t(
          'myProfile.profileSubPage.instagramNotProfessional',
          '개인 계정은 연결할 수 없습니다. 프로페셔널(비즈니스·크리에이터) 계정으로 전환 후 다시 시도해주세요.',
        );
      }
      return t('myProfile.socialConnectCallback.failed', '연결에 실패했습니다.');
    },
    [t],
  );
  // Guard against React StrictMode double-invoking the effect in dev, which
  // would exchange the same one-time auth code twice → second call fails with
  // Google "invalid_grant".
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');
    const errorReason = searchParams.get('error_reason');

    if (error) {
      // Declining at the consent screen is a normal choice, so it is reported
      // to the opener as a cancellation rather than an authorization failure.
      // The opener keeps quiet for this sentinel, the same way it already does
      // for a closed popup. `access_denied` is the shared signal across
      // providers; Facebook also sends error_reason=user_denied.
      const cancelled = error === 'access_denied' || errorReason === 'user_denied';
      postResult(detectPlatform(state), null, cancelled ? CANCELLED : error);
      setStatus('error');
      setMessage(
        cancelled
          ? t('myProfile.socialConnectCallback.cancelled', '취소했습니다.')
          : t('myProfile.socialConnectCallback.failed', '연결에 실패했습니다.'),
      );
      setTimeout(() => window.close(), CLOSE_DELAY_MS.error);
      return;
    }

    if (!code) {
      setStatus('error');
      setMessage(t('myProfile.socialConnectCallback.failed', '연결에 실패했습니다.'));
      setTimeout(() => window.close(), CLOSE_DELAY_MS.error);
      return;
    }

    const platform = detectPlatform(state);
    setMessage(t('myProfile.socialConnectCallback.processing', '연결 중…'));
    exchangeCode(platform, code)
      .then((result) => {
        setStatus('success');
        setMessage(t('myProfile.socialConnectCallback.success', '계정을 연결했습니다.'));
        postResult(platform, result, null);
        setTimeout(() => window.close(), CLOSE_DELAY_MS.success);
      })
      .catch((err) => {
        const raw = err?.message || '';
        setStatus('error');
        // Displayed message is humanised; the opener still receives `raw`.
        setMessage(humanise(raw));
        postResult(platform, null, raw);
        setTimeout(() => window.close(), CLOSE_DELAY_MS.error);
      });
  }, [searchParams, t, humanise]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="text-center p-8">
        {status === 'processing' && (
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
        )}
        {status === 'success' && <div className="text-green-600 text-4xl mb-4">✓</div>}
        {status === 'error' && <div className="text-red-600 text-4xl mb-4">✗</div>}
        <p className="text-gray-700">{message}</p>
      </div>
    </div>
  );
};

function postResult(platform: string, result: any, error: string | null) {
  window.opener?.postMessage({
    type: 'social-connect-result',
    platform,
    result,
    error,
  }, window.location.origin);
}

function detectPlatform(state: string | null): string {
  // The popup is a separate browsing context, so the opener's sessionStorage
  // is not reliably visible here. Primary detection: the platform is encoded
  // as a prefix in the state value (see socialConnect.ts). Fall back to
  // sessionStorage matching for older/in-flight sessions.
  if (state) {
    if (state.startsWith('youtube_') || state.startsWith('google_')) return 'youtube';
    if (state.startsWith('naverblog_') || state.startsWith('naver_')) return 'naverblog';
    if (state.startsWith('instagram_')) return 'instagram';
  }
  if (sessionStorage.getItem('naver_oauth_state') === state) return 'naverblog';
  if (sessionStorage.getItem('instagram_oauth_state') === state) return 'instagram';
  if (sessionStorage.getItem('google_oauth_state') === state) return 'youtube';
  return 'unknown';
}

async function exchangeCode(platform: string, code: string): Promise<SocialConnectResult> {
  // Both values come from build configuration only — no hardcoded fallback,
  // so a misconfigured build fails fast rather than shipping a credential.
  const graphqlEndpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT;
  const apiKey = process.env.REACT_APP_GRAPHQL_API_KEY;
  const redirectUri = `${window.location.origin}/social-connect/callback`;

  if (!graphqlEndpoint) throw new Error('GraphQL endpoint not configured');
  if (!apiKey) throw new Error('GraphQL API key not configured');

  if (platform === 'naverblog') {
    // Reuse the existing naverOAuth mutation
    const res = await fetch(graphqlEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
      body: JSON.stringify({
        query: `mutation NaverOAuth($input: NaverOAuthInput!) {
          naverOAuth(input: $input) { success message error }
        }`,
        variables: { input: { code, redirectUri } },
      }),
    });
    const data = await res.json();
    const result = data?.data?.naverOAuth;
    if (!result?.success) throw new Error(result?.error || 'Naver connect failed');
    const userInfo = JSON.parse(result.message);
    return { platform: 'naverblog', username: userInfo.name || userInfo.email, email: userInfo.email };
  }

  if (platform === 'instagram') {
    // Instagram Login — the backend exchanges the code (server-side app
    // secret) and reads the user's own professional account via the Graph API.
    const res = await graphqlApi.connectInstagramAccount(code, redirectUri);
    if (!res.success || !res.data) {
      throw new Error(res.error || 'Instagram connect failed');
    }
    const d = res.data;
    return {
      platform: 'instagram',
      username: d.username || d.accountId,
      accountId: d.accountId,
      // Must stay `followersCount`: the opener reads that name for Instagram.
      // Posting it as `subscriberCount` (the YouTube field) made the opener see
      // undefined and silently keep the stored value, so a connect that had
      // fetched the real count still displayed the old one.
      followersCount: d.followersCount,
    };
  }

  if (platform === 'youtube') {
    // Google/YouTube OAuth — the backend exchanges the code (server-side
    // client_secret) and reads the user's own channel via channels.list.
    const res = await graphqlApi.connectYoutubeChannel(code, redirectUri);
    if (!res.success || !res.data) {
      throw new Error(res.error || 'YouTube connect failed');
    }
    const d = res.data;
    return {
      platform: 'youtube',
      username: d.handle || d.title || d.channelId,
      channelId: d.channelId,
      title: d.title,
      handle: d.handle,
      subscriberCount: d.subscriberCount,
      hiddenSubscriberCount: d.hiddenSubscriberCount,
    };
  }

  throw new Error(`Unsupported platform: ${platform}`);
}

export default SocialConnectCallback;
